import React from 'react';
import css from './Template2BoldSplit.module.css';
import EditableRichField from '../EditableRichField';

const EditableField = props => <EditableRichField {...props} css={css} />;

const DeleteButton = ({ onClick, title = 'Delete Section' }) => (
  <button className={css.deleteSectionBtn} onClick={onClick} title={title}>
    ×
  </button>
);

const EditWrapper = ({ children, isEditMode, onDelete, className }) => {
  if (!isEditMode) return children;
  return (
    <div className={`${css.editWrapper} ${className || ''}`}>
      {onDelete && <DeleteButton onClick={onDelete} />}
      {children}
    </div>
  );
};

const EditableButton = ({ title, url, onTitleChange, onUrlChange, isEditMode, className, placeholderTitle, placeholderUrl }) => {
  if (isEditMode) {
    return (
      <div className={css.buttonEditContainer}>
        <input
          className={css.editableInput}
          type="text"
          value={title || ''}
          onChange={e => onTitleChange(e.target.value)}
          placeholder={placeholderTitle || 'Button Title'}
        />
        <input
          className={css.editableInput}
          type="text"
          value={url || ''}
          onChange={e => onUrlChange(e.target.value)}
          placeholder={placeholderUrl || 'Button URL'}
        />
      </div>
    );
  } else {
    return (
      <a className={className} href={url} target="_blank" rel="noopener noreferrer">
        {title}
      </a>
    );
  }
};

const Template2BoldSplit = ({ data, onChange, isEditMode, triggerConfirm }) => {
  const handleUpdate = (key, value) => {
    onChange(key, value);
  };

  const handleUpdateCard = (cardIndex, field, value) => {
    const newCards = [...(data?.cards || [])];
    newCards[cardIndex] = { ...newCards[cardIndex], [field]: value };
    onChange('cards', newCards);
  };

  const addCard = () => {
    const newCards = [...(data?.cards || [])];
    newCards.push({
      num: `0${newCards.length + 1}`,
      title: 'Section Title',
      text: 'Your point here, stated cleanly. Two to three sentences is plenty.'
    });
    onChange('cards', newCards);
  };

  const deleteCard = (index) => {
    const newCards = (data?.cards || []).filter((_, i) => i !== index);
    onChange('cards', newCards);
  };

  return (
    <article className={css.t2}>
      <div className={css.t2Hero}>
        <div className={css.t2HeroLeft}>
          <div>
            <EditWrapper isEditMode={isEditMode}>
              <EditableField
                tag="div"
                className={css.t2Badge}
                value={data?.badge}
                onChange={val => handleUpdate('badge', val)}
                isEditMode={isEditMode}
                placeholder="Forteh — Blog Category"
              />
            </EditWrapper>
            
            <EditWrapper isEditMode={isEditMode}>
              <EditableField
                tag="h1"
                className={css.t2Title}
                value={data?.title}
                onChange={val => handleUpdate('title', val)}
                isEditMode={isEditMode}
                multiline
                placeholder="Your Bold Blog Headline Lives Here"
              />
            </EditWrapper>
          </div>
          
          <EditWrapper isEditMode={isEditMode}>
            <div className={css.t2Meta}>
              <EditableField
                tag="span"
                value={data?.readTime}
                onChange={val => handleUpdate('readTime', val)}
                isEditMode={isEditMode}
                placeholder="4 min read"
              />
              &nbsp;·&nbsp;
              <EditableField
                tag="span"
                value={data?.topicTag}
                onChange={val => handleUpdate('topicTag', val)}
                isEditMode={isEditMode}
                placeholder="Topic Tag"
              />
              &nbsp;·&nbsp;
              <EditableField
                tag="span"
                value={data?.website}
                onChange={val => handleUpdate('website', val)}
                isEditMode={isEditMode}
                placeholder="forteh.ca"
              />
            </div>
          </EditWrapper>
        </div>
        
        <div className={css.t2HeroRight}>
          <div className={css.t2Arc}></div>
          <div className={css.t2Arc2}></div>
          
          <EditWrapper isEditMode={isEditMode}>
            <EditableField
              tag="div"
              className={css.t2StatBig}
              value={data?.statNumber}
              onChange={val => handleUpdate('statNumber', val)}
              isEditMode={isEditMode}
              placeholder="60–80%"
            />
          </EditWrapper>
          
          <EditWrapper isEditMode={isEditMode}>
            <EditableField
              tag="div"
              className={css.t2StatLabel}
              value={data?.statLabel}
              onChange={val => handleUpdate('statLabel', val)}
              isEditMode={isEditMode}
              multiline
              placeholder="Lead stat or key proof point for your post"
            />
          </EditWrapper>
        </div>
      </div>

      <div className={css.t2Body}>
        <EditWrapper isEditMode={isEditMode}>
          <EditableField
            tag="p"
            className={css.t2Intro}
            value={data?.intro}
            onChange={val => handleUpdate('intro', val)}
            isEditMode={isEditMode}
            multiline
            placeholder="Your opening paragraph goes here. The bold split hero grabs attention — now hook them with a sharp, confident opening that delivers on the headline's promise."
          />
        </EditWrapper>

        <div className={css.t2Grid}>
          {(data?.cards || []).map((card, index) => (
            <EditWrapper 
              key={index} 
              isEditMode={isEditMode} 
              onDelete={() => triggerConfirm('Delete Card?', `Remove card ${card?.num}?`, () => deleteCard(index))}
            >
              <div className={css.t2Card}>
                <EditableField
                  tag="div"
                  className={css.t2CardNum}
                  value={`${card?.num || ''} —`}
                  onChange={val => handleUpdateCard(index, 'num', val.replace(' —', ''))}
                  isEditMode={isEditMode}
                  placeholder="01 —"
                />
                <EditableField
                  tag="div"
                  className={css.t2CardTitle}
                  value={card?.title}
                  onChange={val => handleUpdateCard(index, 'title', val)}
                  isEditMode={isEditMode}
                  placeholder="Section Title"
                />
                <EditableField
                  tag="p"
                  className={css.t2CardText}
                  value={card?.text}
                  onChange={val => handleUpdateCard(index, 'text', val)}
                  isEditMode={isEditMode}
                  multiline
                  placeholder="Your point here, stated cleanly. Two to three sentences is plenty."
                />
              </div>
            </EditWrapper>
          ))}
        </div>

        {isEditMode && (
          <button className={css.addSectionBtn} onClick={addCard}>
            + Add Card
          </button>
        )}

        <EditWrapper isEditMode={isEditMode}>
          <div className={css.t2Quote}>
            <EditableField
              tag="p"
              value={data?.quote}
              onChange={val => handleUpdate('quote', val)}
              isEditMode={isEditMode}
              multiline
              placeholder="Your most powerful line from the post. The dark background and terracotta border make this unmissable as readers scroll."
            />
          </div>
        </EditWrapper>

        <EditWrapper isEditMode={isEditMode}>
          <div className={css.t2Cta}>
            <div>
              <EditableField
                tag="h3"
                value={data?.ctaTitle}
                onChange={val => handleUpdate('ctaTitle', val)}
                isEditMode={isEditMode}
                placeholder="Ready to take the next step?"
              />
              <EditableField
                tag="p"
                value={data?.ctaSubtext}
                onChange={val => handleUpdate('ctaSubtext', val)}
                isEditMode={isEditMode}
                placeholder="Browse talent or explore more insights at forteh.ca"
              />
            </div>
            <EditableButton
              title={data?.ctaButton}
              url={data?.ctaButtonUrl}
              onTitleChange={val => handleUpdate('ctaButton', val)}
              onUrlChange={val => handleUpdate('ctaButtonUrl', val)}
              isEditMode={isEditMode}
              className={css.t2Btn}
              placeholderTitle="Browse Talent →"
              placeholderUrl="https://forteh.ca"
            />
          </div>
        </EditWrapper>
      </div>
    </article>
  );
};

export default Template2BoldSplit;